import { createElement, MouseEvent, ReactNode, SyntheticEvent } from 'react'
import { splitCommaSeparated } from '@common/helpers/helpers'
import { AuthProvider } from '@common/interfaces/auth'
import { PaymentStatus } from '@common/interfaces/bbc'
import {
  DueDiligenceDocumentRequestStep,
  IDueDiligenceDocumentRequest,
  DUE_DILIGENCE_STATUS_SORT_ORDER,
} from '@common/interfaces/dueDiligence'
import { INewsfeed } from '@common/interfaces/newsfeed'
import {
  workflowMappings,
  crmWorkflowMappings,
  opsWorkflowToSalesforceFieldMapping,
  WorkflowTypes,
} from '@common/interfaces/notes'
import debounce from 'lodash/debounce'
import isDate from 'lodash/isDate'
import isObject from 'lodash/isObject'
import moment from 'moment-timezone'
import { IActionWithParams, IPaginationParams } from 'src/redux/apiMiddleware'
import genericSs from '../assets/styles/generic.module.scss'
import {
  DATE_FORMAT_FULL_YEAR_MOMENT,
  DATE_FORMAT_MOMENT,
  DATE_FORMAT_SHORT_MOMENT,
  DATE_TIME_FORMAT_MOMENT,
  DATE_TIME_FORMAT_MOMENT_FULL,
  TIME_FORMAT_MOMENT,
  DATE_FORMAT_MOMENT_FULL,
  DATE_FORMAT_DOT,
  DATE_DATABASE_FORMAT,
  YEAR_FORMAT_MOMENT,
  MONTH_YEAR_FORMAT_MOMENT,
  MONTH_YEAR_FORMAT_SHORT_MOMENT,
  QUARTER_YEAR_FORMAT_SHORT_MOMENT,
  EASTERN_TIME_ZONE,
} from '../constants/common'
import { isSnapshotRender } from './snapshotMode'

export { default as useRouteChange } from '../hooks/useRouteChange'

export const splitTags = (raw: string): string[] => splitCommaSeparated(raw)

export const getUnlinkErrorMessage = (provider: AuthProvider): string => {
  return `You cannot unlink your ${provider} account because you do not have another authentication method set up. Please set up another method first.`
}

export const getSetupAnotherAuthMethodMessage = (provider: AuthProvider): string => {
  return `You must have another authentication method set up before unlinking your ${provider} account.`
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- generic breaks callers with AutocompleteInputChangeReason etc
export const debounceEventHandler = (func: any, timeout: number) => {
  const debounced = debounce(func, timeout)

  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-argument -- propagates from func
  return (...args: any[]): any => debounced(...args)
}

export const transformNegativeToZero = (amount: number) => (amount < 0 ? 0 : amount)

/**
 * First two CHARACTERS of a name — the entity/company badge form ("Harbor Peak" → "HA").
 *
 * Not for people: a person's initials come from `helpers/avatarPalette`'s `avatarInitials`, which
 * takes the first letter of the first and last word, and is what every avatar in the app renders.
 */
export const getInitials = (name?: string): string => {
  if (!name) return '?'
  return name.substring(0, 2).toUpperCase()
}

// First letter of each of the first two words ("Harbor Peak" → "HP") — the CRM
// avatar-badge style, distinct from getInitials' first-two-characters form.
export const nameInitials = (name?: string | null): string =>
  (name ?? '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('') || '?'

/**
 * Accounting presentation for the PDF export, which goes to a bank: a negative
 * reads as `(1,234.00)`, not `-1,234.00`. Applied in the formatters rather than
 * at the call sites so every figure in the export follows it — the financial
 * statements do not share one cell component.
 */
const withAccountingSign = (formatted: string, value: number) =>
  isSnapshotRender() && value < 0 ? `(${formatted.replace('-', '')})` : formatted

export const formatPrice = (price: unknown, decimals = 2) => {
  const num = Number(price) || 0
  const formatted = (Math.round((num + Number.EPSILON) * 10000) / 10000).toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })
  return withAccountingSign(formatted, num)
}

export const formatPercent = (percent: number, decimalPlaces = 2, scaleValue = 100) =>
  `${(percent * scaleValue || 0).toFixed(decimalPlaces)}%`

export const formatAmount = (amount: unknown, adormnemt = '', clean = false) => {
  if (!amount) {
    return null
  }

  const num = Number(amount)
  const absNum = Math.abs(num)

  const cleanDecimals = (value: number, decimals: number) => {
    const formatted = value.toFixed(decimals)
    return formatted.replace(/\.?0+$/, '')
  }

  // Each tier's ceiling is pulled in by half of the last digit that tier prints, so
  // a value which ROUNDS to a thousand of its unit is promoted rather than shown as
  // "1000" of it. Comparing against a flat 1e6/1e9 meant $999,999 rendered as
  // "1000.0K" instead of "1.00MM", and $999,999,999 as "1000.00MM".
  const kCeiling = 1000000 - 50 // 0.05K, the K tier's rounding step
  const mmCeiling = 1000000000 - (clean ? 50000 : 5000) // 0.05M clean, else 0.005MM

  if (absNum < 1000) {
    return `${adormnemt}${num.toFixed(2)}`
  }
  if (absNum < kCeiling) {
    return `${adormnemt}${clean ? cleanDecimals(num / 1000, 1) : (num / 1000).toFixed(1)}K`
  }
  if (absNum < mmCeiling) {
    return `${adormnemt}${clean ? cleanDecimals(num / 1000000, 1) : (num / 1000000).toFixed(2)}${clean ? 'M' : 'MM'}`
  }
  return `${adormnemt}${clean ? cleanDecimals(num / 1000000000, 1) : (num / 1000000000).toFixed(1)}B`
}

const MILLION = 1000000
const SMALLEST_MILLIONS_STEP = 0.1

/**
 * Always in millions at one decimal ($0.5M, $4.5M, $1,200.0M), for deal-sized amounts read down
 * a column or across a card: a shared unit lets the digits show magnitude, where `formatAmount`'s
 * K/MM switch makes $450.0K and $4.50MM look alike. A non-zero amount too small to show is
 * `<$0.1M`, never `$0.0M`. Null for a missing or zero amount, like `formatAmount`.
 */
export const formatAmountInMillions = (amount: unknown, adornment = '$'): string | null => {
  const num = Number(amount)
  if (!amount || !Number.isFinite(num)) {
    return null
  }
  const sign = num < 0 ? '-' : ''
  const millions = Math.abs(num) / MILLION
  if (millions < SMALLEST_MILLIONS_STEP / 2) {
    return `${sign}<${adornment}${SMALLEST_MILLIONS_STEP}M`
  }
  const formatted = millions.toLocaleString('en-US', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })
  return `${sign}${adornment}${formatted}M`
}

// Lives in common because the server writes figures into note prose, where the full
// "9,180,000.00" a cell shows is unreadable. Re-exported here so the client call sites keep
// their import path.
export {
  formatCompactNumber,
  formatCompactPrice,
  formatSignificantCompactPrice,
} from '@common/helpers/helpers'

export const formatPriceNoDecimal = (price: unknown) => {
  const num = Number(price) || 0
  const formatted = (Math.round((num + Number.EPSILON) * 10000) / 10000).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })
  return withAccountingSign(formatted, num)
}

export const formatPriceConditionalDecimal = (price: unknown, forceDecimal = false) => {
  if (forceDecimal) {
    return formatPrice(price)
  }
  const num = Number(price)
  return Math.abs(num) >= 100 || num === 0 ? formatPriceNoDecimal(price) : formatPrice(price)
}

/* `formatPriceAbbreviated` used to live here — a second condensed-money ladder,
   written for the condensed financial tables before `formatCompactNumber` existed
   for the dense mobile ones. The two agreed on the shape and disagreed on the
   details, and its version was the weaker of the two: it rounded thousands whole
   ($301.7K read as "302K"), kept a `.0` that carries nothing ("2.0M"), and had no
   billions rung at all, so 1.5B printed as "1500.0M". `formatCompactPrice` above is
   now the single implementation. */

export const formatPriceWithSeparatedDecimal = (price: unknown, decimals = 2) => {
  const formattedValue = formatPrice(price, decimals)
  const [integerPart, decimalPart] = formattedValue.split('.')
  return { integerPart, decimalPart }
}

/**
 * Count for an unread/mention badge, capped so a big number can't stretch the pill
 * it sits in — a 4-digit count widens a sidebar row or a 56px rail out of line with
 * its neighbours.
 *
 * Shared because it was written four separate times: once each in the three Messages
 * sidebar components and again as `formatCount` in NotesRail, all byte-identical.
 */
export const formatBadgeCount = (count: number): string => (count > 99 ? '99+' : String(count))

export const formatNumber = (number: unknown, decimals = 0, forceDecimals = false) => {
  if (!number && number !== 0) {
    return null
  }
  return (Math.round(((Number(number) || 0) + Number.EPSILON) * 10000) / 10000).toLocaleString(
    'en-US',
    {
      minimumFractionDigits: forceDecimals ? decimals : 0,
      maximumFractionDigits: decimals,
    },
  )
}

export const unFormatPrice = (price: string | number) => {
  if (!price) {
    return 0
  }

  const parts = (1234.5)
    .toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
    .match(/(\D+)/g)
  let unFormatted = price.toString()

  unFormatted = unFormatted.split(parts[0]).join('')
  unFormatted = unFormatted.split(parts[1]).join('.')

  return parseFloat(unFormatted) || 0
}

const timeZoneDate = (date: string) => moment.utc(date)
export const formatDate = (date: string) => timeZoneDate(date).format(DATE_FORMAT_MOMENT)

export const formatDateShort = (date: string) => timeZoneDate(date).format(DATE_FORMAT_SHORT_MOMENT)

export const formatDateFullYear = (date: string) =>
  timeZoneDate(date).format(DATE_FORMAT_FULL_YEAR_MOMENT)

export const formatDateTime = (date: string) => moment(date).format(DATE_TIME_FORMAT_MOMENT)

export const formatTime = (date: string) => moment(date).format(TIME_FORMAT_MOMENT)

export const formatTextDate = (date: string) => timeZoneDate(date).format(DATE_FORMAT_MOMENT_FULL)

export const formatTextDateLocal = (date: string) => moment(date).format(DATE_FORMAT_MOMENT_FULL)

export const formatUnixDate = (date: number) => moment(date).format(DATE_FORMAT_DOT)

export const formatDateYear = (date: string) => timeZoneDate(date).format(YEAR_FORMAT_MOMENT)

export const formatDateMonthYear = (date: string) =>
  timeZoneDate(date).format(MONTH_YEAR_FORMAT_MOMENT)

export const formatDateMonthYearShort = (date: string) =>
  timeZoneDate(date).format(MONTH_YEAR_FORMAT_SHORT_MOMENT)

/** Strip date part (e.g. " 4/1/22") from header label for consistent cell note columnId. */
export const normalizeColumnIdForNotes = (header: string | undefined): string =>
  header ? header.replace(/(Proj)\s+\d{1,2}\/\d{1,2}\/\d{2,4}(?=\s|$)/, '$1') : ''

export const formatDateQuarterYearShort = (date: string) =>
  timeZoneDate(date).format(QUARTER_YEAR_FORMAT_SHORT_MOMENT)

export const databaseDateFormat = (date: string) => moment(date).format(DATE_DATABASE_FORMAT)

export const formatDateCalendar = (date: string) =>
  moment(date).calendar({
    sameDay: '[Today], h:mm a',
    lastDay: '[Yesterday], h:mm a',
    lastWeek: `[${moment()
      .startOf('day')
      .diff(moment(date).startOf('day'), 'days')} days ago], h:mm a`,
    nextDay: 'M/D/YY, h:mm a',
    nextWeek: 'M/D/YY, h:mm a',
    sameElse: 'M/D/YY, h:mm a',
  })

export const getLatestDate = (
  a: string | null | undefined,
  b: string | null | undefined,
): string | null => {
  if (!a) return b ?? null
  if (!b) return a
  return a >= b ? a : b
}

export const formatDateCalendarNoTime = (date: string, todayTime?: boolean) =>
  moment(date).calendar({
    sameDay: todayTime ? 'h:mm A' : '[Today]',
    lastDay: '[Yesterday]',
    lastWeek: `[${moment().startOf('day').diff(moment(date).startOf('day'), 'days')} days ago]`,
    nextDay: 'M/D/YY',
    nextWeek: 'M/D/YY',
    sameElse: 'M/D/YY',
  })

/**
 * Relative date-group label for newsfeed lists:
 * "Today" | "Yesterday" | "Earlier this week" | "Earlier".
 * Shared by the Market News sidebar and full-page list so both group
 * articles the same way.
 */
export const getNewsGroupLabel = (processedAt: string): string => {
  const date = moment(processedAt)
  if (date.isSame(moment(), 'day')) {
    return 'Today'
  }
  if (date.isSame(moment().subtract(1, 'day'), 'day')) {
    return 'Yesterday'
  }
  if (date.isAfter(moment().subtract(7, 'days').startOf('day'))) {
    return 'Earlier this week'
  }
  return 'Earlier'
}

/**
 * Group a pre-sorted (newest first) list into consecutive date-label groups
 * ("Today", "Yesterday", "Earlier this week", "Earlier") using
 * getNewsGroupLabel. Shared by both news surfaces and Market Intelligence, so
 * every feed breaks on the same boundaries. An item with no date joins the group
 * above it rather than starting an empty one.
 */
export const groupByDateLabel = <T>(
  items: T[] | undefined,
  getDate: (item: T) => string | Date | null | undefined,
): { label: string; items: T[] }[] => {
  const groups: { label: string; items: T[] }[] = []
  items?.forEach((item) => {
    const date = getDate(item)
    const lastGroup = groups[groups.length - 1]
    if (!date) {
      if (lastGroup) {
        lastGroup.items.push(item)
        return
      }
      groups.push({ label: 'Earlier', items: [item] })
      return
    }
    const label = getNewsGroupLabel(String(date))
    if (lastGroup?.label === label) {
      lastGroup.items.push(item)
    } else {
      groups.push({ label, items: [item] })
    }
  })
  return groups
}

export const groupNewsfeedByDate = (
  newsfeed?: INewsfeed[],
): { label: string; items: INewsfeed[] }[] => groupByDateLabel(newsfeed, (item) => item.processedAt)

/**
 * Compact relative-time string for activity feeds / comment timestamps:
 * "just now" | "5m ago" | "3h ago" | "7d ago" | "2y ago".
 * For verbose calendar-style output ("Yesterday, 3:45 pm") use
 * formatDateCalendar / formatDateCalendarNoTime instead.
 */
export const formatShortRelativeAgo = (date: string | Date): string => {
  const then = moment(date)
  if (!then.isValid()) {
    return ''
  }
  const now = moment()
  const minutes = now.diff(then, 'minutes')
  if (minutes < 0) {
    return 'just now'
  }
  if (minutes < 60) {
    return `${minutes}m ago`
  }
  const hours = now.diff(then, 'hours')
  if (hours < 24) {
    return `${hours}h ago`
  }
  const days = now.diff(then, 'days')
  if (days < 365) {
    return `${days}d ago`
  }
  const years = now.diff(then, 'years')
  return `${years}y ago`
}

export const formatDateWithTimezone = (date: string, timezone = EASTERN_TIME_ZONE) =>
  moment(date).tz(timezone).format(DATE_FORMAT_MOMENT)

export const formatTodayTimeOrDate = (date: string) =>
  moment(date).calendar({
    sameDay: TIME_FORMAT_MOMENT,
    lastDay: DATE_FORMAT_MOMENT,
    lastWeek: DATE_FORMAT_MOMENT,
    nextDay: DATE_FORMAT_MOMENT,
    nextWeek: DATE_FORMAT_MOMENT,
    sameElse: DATE_FORMAT_MOMENT,
  })

// Full local date + time (e.g. "Jul 14, 2026, 3:42 PM"), always exposing the
// exact send time regardless of how old the message is. Backs hover tooltips
// where the visible label stays relative/short (see formatTodayTimeOrDate).
// moment() renders in the viewer's local timezone by default.
export const formatFullDateTime = (date: string) =>
  moment(date).format(DATE_TIME_FORMAT_MOMENT_FULL)

export const dateToString = (date: Date | string | null) => {
  if (!date) {
    return null
  }
  date = moment(date).toDate()

  if (typeof date === 'string') {
    return date
  }

  if (isNaN(date?.getTime())) {
    return null
  }

  return [
    date.getFullYear(),
    (date.getMonth() + 1).toString().padStart(2, '0'),
    date.getDate().toString().padStart(2, '0'),
  ].join('-')
}

export const timeToString = (date: Date) =>
  [
    date.getUTCHours().toString().padStart(2, '0'),
    date.getUTCMinutes().toString().padStart(2, '0'),
    date.getUTCSeconds().toString().padStart(2, '0'),
  ].join(':')

export const dateTimeToString = (date: Date) => `${dateToString(date)}T${timeToString(date)}Z`

export const prepareRequestParams = (data: Record<string, unknown>): Record<string, unknown> => {
  if (!isObject(data) || data instanceof FormData) {
    return data
  }
  for (const [key, value] of Object.entries(data)) {
    if (isObject(value)) {
      data[key] = prepareRequestParams(value as Record<string, unknown>)
    } else if (isDate(value)) {
      data[key] = dateToString(value)
    }
  }

  return data
}

export const download = (blob: Blob, name: string) => {
  if (!document) return
  if (blob.type === 'application/json') {
    throw new Error('Download failed. Please contact your system administrator.')
  }

  const a = document.createElement('a')
  const url = window.URL.createObjectURL(blob)
  a.href = url
  a.download = name
  a.click()
  window.URL.revokeObjectURL(url)
}

export const formatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
})

const checkIsInputType = (target: Element) =>
  // Use closest() so clicks on nested content register as an input — e.g. the
  // placeholder/value <span> inside a synthetic select/input cell sits more than
  // one level deep, which a shallow target/parent check misses (clicking the
  // placeholder text would otherwise collapse a multi-selection).
  !!target.closest('.syntheticInputField') ||
  !!target.closest('.syntheticSelectField') ||
  target.tagName === 'INPUT'

// True when focus is inside an editable element — `<input>`, `<textarea>`, or a
// `contenteditable` host (draft-js note/commentary editors use a contenteditable
// div, not an <input>). Used to suppress global arrow-key handlers (e.g. note/flag
// list navigation) while the user is typing so the cursor moves within the text.
export const isEditableElementFocused = (): boolean => {
  const active = document.activeElement as HTMLElement | null
  return (
    active?.tagName === 'INPUT' ||
    active?.tagName === 'TEXTAREA' ||
    active?.isContentEditable === true
  )
}

// Inclusive numeric range between two indices — the index-keyed shift range.
export const numericShiftRange = (anchor: number, clicked: number): number[] => {
  const length = Math.abs(anchor - clicked) + 1
  return Array.from({ length }, (_, idx) => (anchor <= clicked ? anchor + idx : anchor - idx))
}

// Inclusive range of items between two keys in an ordered list — the resource
// (id-keyed) shift range. Positions are looked up in the list rather than derived
// arithmetically. Returns [] if either key isn't present.
export const keyedShiftRange = <TItem, K>(
  items: TItem[],
  getKey: (item: TItem) => K,
  anchorKey: K,
  clickedKey: K,
): TItem[] => {
  const anchorPos = items.findIndex((i) => getKey(i) === anchorKey)
  const clickedPos = items.findIndex((i) => getKey(i) === clickedKey)
  if (anchorPos < 0 || clickedPos < 0) return []
  const [lo, hi] = anchorPos <= clickedPos ? [anchorPos, clickedPos] : [clickedPos, anchorPos]
  return items.slice(lo, hi + 1)
}

// Multi-select click handler, generic over the selected item's key. Index-keyed
// tables pass an identity `getKey` + `numericShiftRange` (the legacy behavior);
// resource-keyed selection (useTable's `selection` mode) passes an id accessor +
// an ordered-list range. Cmd/Ctrl toggles, Shift selects the range from the
// anchor, a plain click selects one.
export const handleMultipleSelect =
  <TItem, K>(
    handleSelectItems: (items: ((prev: TItem[]) => TItem[]) | TItem[]) => void,
    handleSelectItem: (key: K) => void,
    activeItems: TItem[],
    previousActiveKey: K | null,
    getKey: (item: TItem) => K,
    shiftRange: (anchorKey: K, clickedKey: K) => TItem[],
  ) =>
  (event: MouseEvent<HTMLTableCellElement | HTMLTableRowElement>, item: TItem) => {
    const { ctrlKey, shiftKey, metaKey, target } = event
    if (!(target instanceof Element)) {
      return
    }
    const tagName = target.tagName.toUpperCase()
    if (metaKey && tagName === 'A') {
      return
    }
    event.preventDefault()

    const key = getKey(item)
    const isInputType = checkIsInputType(target)
    const isSelected = activeItems.some((i) => getKey(i) === key)

    if (
      target.classList?.contains('MuiAutocomplete-option') ||
      target.closest('.MuiAutocomplete-paper') ||
      // Select cells: the widget has no FormControl wrapper, so the value area
      // (.MuiSelect-select / its input base) and the portaled dropdown items
      // (.MuiMenuItem-root) aren't covered by the FormControl/input guards below.
      // Without these, clicking the select box or picking an item bubbles to this
      // row onClick and collapses a multi-selection, breaking bulk edits of select
      // cells (e.g. AR eligibility). Mirrors the Autocomplete guards above and the
      // .MuiSelect-select / .MuiMenuItem-root guards in TableCell.handleClickInside.
      target.closest('.MuiInputBase-root') ||
      target.closest('.MuiMenuItem-root') ||
      target.closest('.MuiMenu-paper') ||
      // Dismissing a popup rendered inside a row: MUI's invisible backdrop is a
      // React-tree descendant of whatever rendered the popover, so the click that
      // closes a per-row menu bubbles to this row onClick and collapses the
      // multi-selection. A backdrop click is a dismissal, never a row selection.
      target.closest('.MuiBackdrop-root') ||
      target.closest('.MuiPickersDay-root') ||
      target.closest('.MuiSvgIcon-root') ||
      target.closest('.MuiIconButton-root') ||
      target.closest('.MuiFormControl-root') ||
      (isSelected && isInputType)
    ) {
      return
    }
    if (metaKey || ctrlKey) {
      handleSelectItems((items) =>
        items.some((i) => getKey(i) === key)
          ? items.length === 1
            ? [item]
            : items.filter((i) => getKey(i) !== key)
          : [...items, item],
      )
      if (!isSelected) {
        handleSelectItem(key)
      }
    } else if (shiftKey && previousActiveKey !== null) {
      // Existing selection plus everything between anchor and clicked, deduped by
      // key (order: existing first, then the new range).
      const between = shiftRange(previousActiveKey, key)
      handleSelectItems((items) => {
        const byKey = new Map(items.map((i) => [getKey(i), i]))
        between.forEach((i) => byKey.set(getKey(i), i))
        return Array.from(byKey.values())
      })
      handleSelectItem(key)
    } else {
      handleSelectItems([item])
      handleSelectItem(key)
    }
  }

/**
 * The column indexes a given row actually renders, scoped to the table owning
 * `root` (which may be the `<table>` itself or any element inside it — an event
 * target works).
 *
 * Needed because a selection rectangle is derived arithmetically from its corners
 * and can therefore span positions the table doesn't render: column indexes are
 * stable per column, so a table that hides one (Ineligible/Eligible on the
 * client-user and LCR pages) leaves a gap. Without filtering, those hidden columns
 * enter the selection and their values leak into the aggregate tooltip.
 *
 * Returns a Set for the whole row rather than answering one cell at a time so
 * callers can hoist it out of their column loop — a drag re-derives its rectangle
 * on every `mouseenter`, and one DOM query per candidate cell would make wide
 * selections crawl.
 *
 * The ownership comparison matters for nested tables: the BBC AR/AP detail tables
 * live inside a `<td>` of their summary table, so a descendant query from the outer
 * table also reaches the nested table's cells.
 */
export const renderedColumnIndexesForRow = (
  root: Element | null | undefined,
  rowIndex: number,
): Set<number> => {
  const columnIndexes = new Set<number>()
  const table = root?.closest('table')
  if (!table) {
    return columnIndexes
  }
  table
    .querySelectorAll(`.activableRow[data-index="${rowIndex}"] .activableCell[data-index]`)
    .forEach((cell) => {
      if (cell.closest('table') !== table) {
        return
      }
      const columnIndex = Number(cell.getAttribute('data-index'))
      if (Number.isInteger(columnIndex)) {
        columnIndexes.add(columnIndex)
      }
    })
  return columnIndexes
}

/**
 * Re-key a cell selection from column-major to field-major.
 *
 * `useTable` tracks selection as `activeCells[rowIndex] = [columnIndex]`, while
 * `useTableSummary` expects `[fieldIndex] = [rowIndex]`, where `fieldIndex` is the
 * position of the field within `cellIndexesMap`'s values — the same order as the
 * `fieldsWithTypes` array the caller hands the hook. Columns absent from the map
 * (labels, counts, and other non-aggregated columns) are dropped.
 */
export const transposeActiveCellsByField = (
  activeCells: number[][],
  cellIndexesMap: Record<number, string>,
): number[][] => {
  const fields = Object.values(cellIndexesMap)
  const transposed = fields.map<number[]>(() => [])
  activeCells.forEach((rowCells, rowIndex) => {
    rowCells?.forEach((columnIndex) => {
      const fieldIndex = fields.indexOf(cellIndexesMap[columnIndex])
      if (fieldIndex >= 0) {
        transposed[fieldIndex].push(rowIndex)
      }
    })
  })
  return transposed
}

export const handleMultipleCellSelect =
  (
    handleSelectCells: (indexes: ((indexes: number[][]) => number[][]) | number[][]) => void,
    reset?: () => void,
  ) =>
  (event: MouseEvent<HTMLTableCellElement>, rowIndex: number, columnIndex: number) => {
    event.preventDefault()
    const { ctrlKey, shiftKey, metaKey, target } = event
    if (!(target instanceof Element)) {
      return
    }
    if (
      target.classList?.contains('MuiAutocomplete-option') ||
      target.closest('.MuiAutocomplete-paper') ||
      target.closest('.MuiPickersDay-root') ||
      target.closest('.MuiSvgIcon-root') ||
      target.closest('.MuiIconButton-root') ||
      target.closest('.MuiFormControl-root')
    ) {
      return
    }
    if (metaKey || ctrlKey) {
      handleSelectCells((indexes: number[][]) => {
        const result = [...(indexes || [])]
        if (!result[rowIndex]) {
          result[rowIndex] = []
        }

        result[rowIndex] = result[rowIndex].includes(columnIndex)
          ? result[rowIndex].filter((item) => item !== columnIndex)
          : [...result[rowIndex], columnIndex]

        return result
      })
    } else if (shiftKey) {
      handleSelectCells((indexes: number[][]) => {
        const result = [...(indexes || [])]
        if (!result[rowIndex]) {
          result[rowIndex] = []
        }
        if (!result.flat().length) {
          result[rowIndex] = [columnIndex]
          return result
        }
        const minRowIndex = Math.min(
          result.findIndex((indexes) => !!indexes?.filter((index) => index !== undefined).length),
          rowIndex,
        )
        const maxRowIndex = Math.max(
          result.findLastIndex(
            (indexes: number[]) => !!indexes?.filter((index) => index !== undefined).length,
          ),
          rowIndex,
        )
        const minColumnIndex = Math.min(
          columnIndex,
          ...result.flat().filter((index) => index !== undefined),
        )
        const maxColumnIndex = Math.max(
          columnIndex,
          ...result.flat().filter((index) => index !== undefined),
        )

        for (let i = minRowIndex; i <= maxRowIndex; i++) {
          const renderedColumns = renderedColumnIndexesForRow(target, i)
          result[i] = Array(maxColumnIndex - minColumnIndex + 1)
            .fill(undefined)
            .map((_, idx) => minColumnIndex + idx)
            .filter((columnIndex) => renderedColumns.has(columnIndex))
        }

        return result
      })
    } else {
      const result = []
      result[rowIndex] = [columnIndex]
      handleSelectCells(result)
    }
    if (reset) {
      reset()
    }
  }

/** Build a Set-based lookup from activeCells for O(1) membership checks. */
export type ActiveCellsLookup = Map<number, Set<number>>

export const buildActiveCellsLookup = (activeCells: number[][]): ActiveCellsLookup => {
  const lookup: ActiveCellsLookup = new Map()
  for (let i = 0; i < activeCells.length; i++) {
    const row = activeCells[i]
    if (row && row.length > 0) {
      lookup.set(i, new Set(row))
    }
  }
  return lookup
}

/** Compute the bottom-right cell position once for tooltip placement. */
export const getTooltipPosition = (
  activeCells: number[][],
): { row: number; col: number } | null => {
  let maxRow = -1
  let maxColInMaxRow = -1

  for (let i = 0; i < activeCells.length; i++) {
    const row = activeCells[i]
    if (row && row.length > 0) {
      if (i > maxRow) {
        maxRow = i
        maxColInMaxRow = -1
      }
      if (i === maxRow) {
        for (let j = 0; j < row.length; j++) {
          if (row[j] > maxColInMaxRow) maxColInMaxRow = row[j]
        }
      }
    }
  }

  return maxRow >= 0 ? { row: maxRow, col: maxColInMaxRow } : null
}

export const getCellSelectionBorderClasses = (
  lookup: ActiveCellsLookup,
  rowIndex: number,
  columnIndex: number,
): string[] => {
  const currentRow = lookup.get(rowIndex)
  if (!currentRow?.has(columnIndex)) {
    return []
  }

  const classes: string[] = []

  if (!lookup.get(rowIndex - 1)?.has(columnIndex)) {
    classes.push('selectionTop')
  }
  if (!lookup.get(rowIndex + 1)?.has(columnIndex)) {
    classes.push('selectionBottom')
  }
  if (!currentRow.has(columnIndex - 1)) {
    classes.push('selectionLeft')
  }
  if (!currentRow.has(columnIndex + 1)) {
    classes.push('selectionRight')
  }

  return classes
}

/** @deprecated Use buildActiveCellsLookup + getTooltipPosition instead */
export const shouldShowMultiCellTooltip = (
  activeCells: number[][],
  rowIndex: number,
  columnIndex: number,
): boolean => {
  const pos = getTooltipPosition(activeCells)
  return pos !== null && pos.row === rowIndex && pos.col === columnIndex
}

export const handleMultipleActivableSelect =
  ({
    handleSelectItems,
    handleSelectItem,
    reset,
    activeIndexes,
    previousActiveItem,
  }: {
    handleSelectItems: (indexes: ((indexes: number[]) => number[]) | number[]) => void
    handleSelectItem: (index: number) => void
    reset: () => void
    activeIndexes: number[]
    // Absent until the first selection, and cleared whenever the rendered dataset
    // changes — Shift then has no anchor to range from.
    previousActiveItem: number | null | undefined
  }) =>
  (event: MouseEvent<HTMLTableCellElement | HTMLTableRowElement>, index: number) => {
    event.preventDefault()
    const { ctrlKey, shiftKey, metaKey, target } = event
    if (!(target instanceof Element)) {
      return
    }
    const isInputType = checkIsInputType(target) || target?.tagName === 'A'

    if (activeIndexes?.includes(index) && isInputType) {
      return
    }

    if (metaKey || ctrlKey) {
      handleSelectItems((indexes: number[]) =>
        indexes.includes(index)
          ? indexes.length === 1
            ? [index]
            : indexes.filter((item) => item !== index)
          : [...indexes, index],
      )
      if (!activeIndexes.includes(index)) {
        handleSelectItem(index)
      }
    } else if (shiftKey && previousActiveItem !== null && previousActiveItem !== undefined) {
      // Without an anchor this branch would compute `index - undefined + 1` = NaN,
      // making `Array.from({ length: NaN })` empty and selecting nothing at all —
      // not even the clicked row. Fall through to the plain single-select instead,
      // mirroring the `previousActiveKey !== null` guard in handleMultipleSelect.
      const isPreviousActiveItemGreaterThanIndex = previousActiveItem > index

      // select all that are already selected and add any in between the previous active item and the current index which can be both ascending or descending
      const indexsToAdd = Array.from(
        {
          length: isPreviousActiveItemGreaterThanIndex
            ? previousActiveItem - index + 1
            : index - previousActiveItem + 1,
        },
        (_, idx) =>
          isPreviousActiveItemGreaterThanIndex
            ? previousActiveItem - idx
            : previousActiveItem + idx,
      )
      const finalIndexes = Array.from(new Set([...activeIndexes, ...indexsToAdd]))
      handleSelectItems(finalIndexes)
      handleSelectItem(index)
    } else {
      handleSelectItems([index])
      handleSelectItem(index)
    }
    if (reset) {
      reset()
    }
  }

export const currentWorkFlow = (
  pathname: string,
  isPortfolio?: boolean,
  isAdmin?: boolean,
  isBDO?: boolean,
): WorkflowTypes => {
  const path = pathname.split('/')
  const pathKey = path[1]

  // CRM detail pages (/crm/companies/:id, /crm/people/:id, /crm/organizations/:id) —
  // only the detail pages get a notes workflow, not the /crm list pages
  if (pathKey === 'crm') {
    const crmKey = path[2]
    if (crmKey && crmKey in crmWorkflowMappings && path[3]) {
      return crmWorkflowMappings[crmKey as keyof typeof crmWorkflowMappings]
    }
    return undefined
  }

  // Use type-safe property access with 'in' operator
  if (pathKey && pathKey in workflowMappings) {
    return workflowMappings[pathKey as keyof typeof workflowMappings]
  } else if (pathKey === '' && (isPortfolio || isAdmin || isBDO)) {
    return WorkflowTypes.homePage
  }
}

/**
 * Notes workflow for a browser location, including the query string. Mirrors
 * {@link currentWorkFlow}, but routes the unified organization page's
 * entity-sourced variant (`/organization/:id?source=entity`, where `:id` is an
 * `entity_info` id, not an organization id) through the Entity Page workflow so
 * notes resolve against the entity instead of throwing "Organization was not found".
 */
export const getNotesWorkflow = (pathname: string, search?: string): WorkflowTypes => {
  const workflow = currentWorkFlow(pathname)
  if (
    workflow === WorkflowTypes.organizationPage &&
    search &&
    new URLSearchParams(search).get('source') === 'entity'
  ) {
    return WorkflowTypes.entityPage
  }
  return workflow
}

export const secondaryWorkFlow = (pathname: string): string => {
  const path = pathname.split('/')
  const lastEl = path[path.length - 1]

  // Use type-safe property access with 'in' operator
  if (lastEl && lastEl in opsWorkflowToSalesforceFieldMapping) {
    return opsWorkflowToSalesforceFieldMapping[
      lastEl as keyof typeof opsWorkflowToSalesforceFieldMapping
    ]
  }
  return opsWorkflowToSalesforceFieldMapping.summary
}

/**
 * Narrows a MUI Autocomplete onChange value (typed broadly because of freeSolo /
 * multiple) to the selected place option's string `value` (the Google place id),
 * or null when nothing valid is selected. Shared by both AutocompletePlace variants.
 */
export const getSelectedPlaceId = (data: unknown): string | null => {
  if (!data || typeof data !== 'object' || !('value' in data) || typeof data.value !== 'string') {
    return null
  }
  return data.value
}

export function loadingArrayReducer(prefixes: string[], itemsToIgnore: string[] = []) {
  const request = new RegExp(`^(?:${prefixes.join('|')}).+request$`, 'i')
  const successOrFailure = new RegExp(`^(?:${prefixes.join('|')}).+(?:success|failure)$`, 'i')
  let ignore: RegExp
  if (itemsToIgnore.length) {
    ignore = new RegExp(
      `${itemsToIgnore.map((item) => item.replace(/_(request|success|failure)/gi, '')).join('|')}`,
      'i',
    )
  }

  return (state: string[] = [], action: IActionWithParams<IPaginationParams>) => {
    if (ignore && ignore.test(action.type)) {
      return state
    }
    if (action.params?.skipLoader) {
      return state
    }
    if (request.test(action.type)) {
      return [...state, action.type]
    } else if (successOrFailure.test(action.type)) {
      const type = action.type.replace('SUCCESS', 'REQUEST').replace('FAILURE', 'REQUEST')
      return state.filter((item) => item !== type)
    }
    return state
  }
}
export const hexToRgbAOpaq = (hex: string) => {
  let c: string[] | number
  if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
    c = hex.substring(1).split('')
    if (c.length === 3) {
      c = [c[0], c[0], c[1], c[1], c[2], c[2]]
    }
    c = parseInt('0x' + c.join(''), 16)
    return 'rgba(' + [(c >> 16) & 255, (c >> 8) & 255, c & 255].join(',') + ',.6)'
  } else {
    return 'rgba(0,0,0,.6)'
  }
}

export const visibilityChangeEventHandler = (callback: (beacon?: boolean) => void) => {
  const handleVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      callback(true)
    }
  }
  document.addEventListener('visibilitychange', handleVisibilityChange, false)
  return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
}

export const handleStopPropagation = (event: SyntheticEvent) => event.stopPropagation()

/**
 * Swallows clicks that reached this element from a portal rather than from its own
 * subtree. A MUI Menu renders its backdrop and paper into `document.body`, but React
 * still bubbles their events through the React tree — so a menu rendered inside a
 * table row fires that row's `onClick` when it is dismissed. Clicks that really did
 * start inside the element (the trigger) are left alone. Same condition the Major
 * Developments rows guard on, expressed for an ancestor rather than the handler itself.
 */
export const handleStopPortalPropagation = (event: MouseEvent) => {
  if (!event.currentTarget.contains(event.target as Node)) {
    event.stopPropagation()
  }
}

/**
 * For a button sitting next to a rich-text editor. Mousedown would move focus off
 * the editor, collapsing its focus-only toolbar; the button then shifts before
 * mouseup and the click never fires, so the first press only blurred the editor.
 */
export const preventEditorBlur = (event: MouseEvent) => event.preventDefault()

export const voidHandler = () => {}

export const formatDateRange = (
  dates: {
    startDate: string
    endDate: string
  }[],
  dateFormat: string = 'MMM YY',
  withCount: boolean = true,
  withWhiteSpace: boolean = false,
): string => {
  if (!dates.length) {
    return ''
  }

  const format = (startDate: string, endDate: string): string => {
    const startDateMoment = moment(startDate)
    const endDateMoment = moment(endDate)
    return startDateMoment.isSame(endDateMoment, 'month')
      ? startDateMoment.format(dateFormat)
      : `${startDateMoment.format(dateFormat)}${withWhiteSpace ? ' ' : ''}-${
          withWhiteSpace ? ' ' : ''
        }${endDateMoment.format(dateFormat)}`
  }
  const formattedDates: string[] = []
  let count = 0

  dates.sort((a, b) => a.startDate.localeCompare(b.startDate))
  dates = dates.map(({ startDate, endDate }) => ({
    startDate: moment(startDate).startOf('month').format('YYYY-MM-DD'),
    endDate: moment(endDate).endOf('month').format('YYYY-MM-DD'),
  }))

  let currentStart = dates[0].startDate
  let currentEnd = dates[0].endDate

  for (let i = 1; i < dates.length; i++) {
    const next = dates[i]

    if (moment(next.startDate).isAfter(moment(currentEnd).add(1, 'day'))) {
      count += Math.ceil(moment(currentEnd).diff(moment(currentStart), 'months', true))
      formattedDates.push(format(currentStart, currentEnd))
      currentStart = next.startDate
    }

    currentEnd = next.endDate > currentEnd ? next.endDate : currentEnd
  }

  count += Math.ceil(moment(currentEnd).diff(moment(currentStart), 'months', true))
  formattedDates.push(format(currentStart, currentEnd))

  if (withCount) {
    return `${count} (${formattedDates.join(', ')})`
  }

  return formattedDates.join(', ')
}

export const highlightMatchesJSX = (
  str: string | number,
  find: string,
  options?: { tag?: string; className?: string },
): ReactNode => {
  const text = str?.toString() || ''
  if (!find) {
    return text
  }

  const words = find
    .replace(/\$/g, '')
    .split(' ')
    .map((word) => word.trim())
    .filter(Boolean)
    .map((word) => word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))

  if (words.length === 0) {
    return text
  }

  const pattern = new RegExp(`(${words.join('|')})`, 'gi')
  const parts = text.split(pattern)
  const testPattern = new RegExp(`^(${words.join('|')})$`, 'i')

  const tag = options?.tag || 'strong'
  const highlightProps = { className: options ? options.className : genericSs.highlightMatch }

  return createElement(
    'span',
    null,
    ...parts.map((part, index) =>
      testPattern.test(part) ? createElement(tag, { key: index, ...highlightProps }, part) : part,
    ),
  )
}

export const sortAndGroupDueDiligenceDocumentsRequests = (
  documentRequests: IDueDiligenceDocumentRequest[],
) => {
  const documents = documentRequests.sort((a, b) => {
    if (a?.type?.subStep === 'Priority' && b?.type?.subStep !== 'Priority') return -1
    if (a?.type?.subStep !== 'Priority' && b?.type?.subStep === 'Priority') return 1

    return DUE_DILIGENCE_STATUS_SORT_ORDER[a?.status] - DUE_DILIGENCE_STATUS_SORT_ORDER[b?.status]
  })

  const samples = documents.filter((documentRequest) => documentRequest?.type?.isSample)

  const companyBackground = documents.filter(
    (documentRequest) =>
      !documentRequest?.type?.isSample &&
      documentRequest?.type?.step === DueDiligenceDocumentRequestStep.CompanyBackground,
  )

  const team = documents.filter(
    (documentRequest) =>
      !documentRequest?.type?.isSample &&
      documentRequest?.type?.step === DueDiligenceDocumentRequestStep.Team,
  )

  const financials = documents.filter(
    (documentRequest) =>
      !documentRequest?.type?.isSample &&
      documentRequest?.type?.step === DueDiligenceDocumentRequestStep.Financials,
  )

  return {
    companyBackground,
    team,
    financials,
    samples,
  }
}

export const formatValue = ({
  value,
  type,
  nullCondition,
  scaleValue,
}: {
  value: unknown
  type: string
  nullCondition?: boolean
  scaleValue?: number
}) => {
  if (nullCondition || value == null || value === '') {
    return '-'
  }
  switch (type) {
    case 'date':
      return formatDate(value as string)
    case 'amount':
    case 'currency':
      return `$${formatPrice(+(value as string | number))}`
    case 'percent':
      return formatPercent(value as number, 2, scaleValue)
    case 'number':
      return formatNumber(value, 2, true)
    case 'boolean':
      return value ? 'Yes' : 'No'
    default:
      return value as string
  }
}

export const createRequestTypes = (prefix: string, base: string) => {
  const types = ['REQUEST', 'SUCCESS', 'FAILURE']
  const prefixAndBase = `${prefix}/${base}`
  return types.map((type) => `${prefixAndBase}_${type}`) as [string, string, string]
}

export const getRGBFFromHex = (hex: string, opacity: number) => {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return `rgba(${r}, ${g}, ${b}, ${opacity})`
}

export const isLocalhost = () =>
  Boolean(
    window.location.hostname === 'localhost' ||
    // [::1] is the IPv6 localhost address.
    window.location.hostname === '[::1]' ||
    // 127.0.0.1/8 is considered localhost for IPv4.
    window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/),
  )

export const formatMaxNumber = (value: number, max: number, hideNegative = true) => {
  if (value) {
    if (hideNegative && value < 0) {
      return '-'
    }
    if (value > max) {
      return `${max}+`
    }
    return value.toFixed(2)
  }
  return '-'
}

export const formatSeconds = (decimalSeconds: number): string => {
  const minutes = Math.floor(decimalSeconds / 60)
  const seconds = Math.floor(decimalSeconds % 60)
  return `${minutes}:${seconds.toString().padStart(2, '0')}`
}

export const calculateGrowthPercentage = (
  currentValue: number,
  previousValue: number,
): number | undefined => {
  if (previousValue !== 0 && !isNaN(previousValue) && !isNaN(currentValue)) {
    return (currentValue - previousValue) / previousValue
  }
  return undefined
}

export const calculateAccountActivityDiscrepancy = (clientCollateral: {
  priorLoanAmount?: number
  realizedInterest?: number
  miscFees?: number
  collections?: number
  adjustments?: number
  returnedCollections?: number
  termLoanPrincipal?: number
  currentLoanAmount?: number
  totalCurrentLoanBalance?: number
  termLoanBalance?: number
}): number => {
  return +(
    clientCollateral?.priorLoanAmount +
    clientCollateral?.realizedInterest +
    clientCollateral?.miscFees -
    clientCollateral?.collections +
    clientCollateral?.adjustments +
    clientCollateral?.returnedCollections +
    (clientCollateral?.termLoanPrincipal ?? 0) -
    clientCollateral?.currentLoanAmount +
    clientCollateral?.totalCurrentLoanBalance -
    clientCollateral?.currentLoanAmount -
    (clientCollateral?.termLoanBalance ?? 0)
  ).toFixed(2)
}

export const getTruncationData = (text: string, baseMaxLength: number, ineligible: boolean) => {
  const maxLength = baseMaxLength + (ineligible ? -10 : 0)
  const shouldTruncate = text.length > maxLength
  const truncatedText = shouldTruncate ? `${text.substring(0, maxLength)}...` : text

  return {
    displayText: truncatedText,
    shouldShowTooltip: shouldTruncate,
  }
}

// Shared with the term sheet generator, which fetches the same stored logo urls server-side.
export { addLogoDevToken } from '@common/helpers/helpers'

export const getPaymentStatusLabel = (invoice: {
  paymentStatus: PaymentStatus
  hasWirePayment?: boolean
}): string => {
  if (invoice.paymentStatus === PaymentStatus.Paid) {
    return invoice.hasWirePayment ? PaymentStatus.Paid : PaymentStatus.ImpliedPaid
  }
  return invoice.paymentStatus
}

export const checkIsInIFrame = () => {
  try {
    return window.self !== window.top
  } catch {
    return true
  }
}

export const isNotNullOrUndefined = (value: number | null | undefined) => {
  return value !== null && value !== undefined
}

/**
 * Determines the column title for BBC tables based on whether the date matches the current BBC being viewed
 * @param date - The date of the column
 * @param currentBBCDate - The recordDate of the current BBC being viewed (from bbc.recordDate)
 * @returns 'Current BBC' if the date matches the current BBC date, otherwise formatted date
 */
export const getBBCColumnTitle = (date: string, currentBBCDate?: string): string => {
  // Current BBC is when the column date matches the BBC we're currently viewing
  return currentBBCDate && date === currentBBCDate ? 'Current BBC' : formatDate(date)
}

export enum ChartPeriods {
  Daily = 'Daily',
  Weekly = 'Weekly',
  Monthly = 'Monthly',
  Quarterly = 'Quarterly',
  Annually = 'Annually',
}

export const CHART_PERIOD_OPTIONS = [
  { value: ChartPeriods.Daily, label: 'Daily' },
  { value: ChartPeriods.Weekly, label: 'Weekly' },
  { value: ChartPeriods.Monthly, label: 'Monthly' },
  { value: ChartPeriods.Quarterly, label: 'Quarterly' },
  { value: ChartPeriods.Annually, label: 'Annually' },
]
/**
 * Time scale config for Chart.js (date-fns displayFormats).
 * Omits year when range is same year.
 */
export function getTimeScaleConfig(
  period: ChartPeriods,
  dateFrom: string,
  dateTo: string,
): { unit: 'day' | 'week' | 'month' | 'quarter' | 'year'; displayFormats: Record<string, string> } {
  const sameYear = !!dateFrom && !!dateTo && moment(dateFrom).year() === moment(dateTo).year()

  switch (period) {
    case ChartPeriods.Daily:
      return {
        unit: 'day',
        displayFormats: {
          day: sameYear ? 'MMM d' : 'MMM d yy',
          week: sameYear ? 'MMM d' : 'MMM d yy',
          month: 'MMM yyyy',
          year: 'yyyy',
        },
      }
    case ChartPeriods.Weekly:
      return {
        unit: 'week',
        displayFormats: {
          week: sameYear ? 'MMM d' : 'MMM d yy',
          month: 'MMM yyyy',
          year: 'yyyy',
        },
      }
    case ChartPeriods.Quarterly:
      return {
        unit: 'quarter',
        displayFormats: {
          quarter: sameYear ? 'QQQ' : 'QQQ yyyy',
          month: 'MMM yyyy',
          year: 'yyyy',
        },
      }
    case ChartPeriods.Annually:
      return {
        unit: 'year',
        displayFormats: { year: 'yyyy', month: 'MMM yyyy' },
      }
    case ChartPeriods.Monthly:
    default:
      return {
        unit: 'month',
        displayFormats: {
          month: sameYear ? 'MMM' : 'MMM yyyy',
          quarter: sameYear ? 'QQQ' : 'QQQ yyyy',
          year: 'yyyy',
        },
      }
  }
}

/** A financials table keeps at least this many period columns, so a short statement fills it. */
const MIN_PERIOD_COLUMNS = 6

/** Keys for the empty columns padding a table of `count` periods out to the minimum. */
export const paddingColumns = (count: number) =>
  Array.from(Array(Math.max(MIN_PERIOD_COLUMNS - count, 0)).keys())

/**
 * Drive one horizontally-scrolling table from another that is not the same width,
 * by the fraction scrolled rather than the pixels: copied as pixels, the narrower
 * range clamps the wider one and its last columns can't be reached. Tables of equal
 * width land on the same offset either way.
 */
export const syncCoupledScroll = (source: HTMLElement, coupledTableIds: string[]) => {
  const sourceRange = source.scrollWidth - source.clientWidth
  // A table with nothing to scroll has no position to share, and dividing by its
  // zero range would put NaN into every coupled table's scrollLeft.
  const ratio = sourceRange > 0 ? source.scrollLeft / sourceRange : 0

  coupledTableIds.forEach((tableId) => {
    const coupledTable = document.getElementById(tableId)
    if (!coupledTable) {
      return
    }
    coupledTable.scrollLeft = ratio * (coupledTable.scrollWidth - coupledTable.clientWidth)
  })
}
